import { Form } from '../models/Form';
import { Step } from '../models/Step';
import { Section } from '../models/Section';
import { Group } from '../models/Group';
import { Question } from '../models/Question';
import type { FormSchema, GroupSchema, GroupSetSchema } from '../types';
import { isGroupSetSchema as isGroupSet } from '../types';
import type { SavedDataLoader } from './savedDataLoader';
import type { IDisplayService } from './displayService';

export class FormService {
  private form: Form;
  private activeStep: Step | null = null;

  constructor(
    private guid: string,
    private savedDataLoader: SavedDataLoader,
    private stateService: { loadedSavedData: boolean }
  ) {
    this.form = new Form(guid);

    this.savedDataLoader.load();

    if (this.savedDataLoader.hasLoadedData()) {
      this.form.guid = this.savedDataLoader.getFormGuid();
      this.stateService.loadedSavedData = true;
    }
  }

  public initializeFromSchema(schema: FormSchema, groupCounts: Record<string, number> = {}): void {
    schema.steps.forEach((stepSchema) => {
      const step = this.createStep(
        stepSchema.id,
        stepSchema.label,
        stepSchema.title,
        stepSchema.navigationLabel
      );

      stepSchema.sections.forEach((sectionSchema) => {
        const section = this.createSection(sectionSchema.id, sectionSchema.label || '', step);

        sectionSchema.groups?.forEach((groupOrSet) => {
          if (isGroupSet(groupOrSet)) {
            const count =
              groupCounts[groupOrSet.groupSetId] ??
              this.savedDataLoader.getNumOfGroups(groupOrSet.group.id, groupOrSet.min ?? 1);

            for (let i = 0; i < count; i++) {
              this.createGroupFromSchema(groupOrSet.group, i, section);
            }
          } else {
            this.createGroupFromSchema(groupOrSet, 0, section);
          }
        });
      });
    });
  }

  private createGroupFromSchema(groupSchema: GroupSchema, index: number, section: Section): Group {
    const group = this.createGroup(groupSchema.id, groupSchema.label || '', index, section);

    groupSchema.fields.forEach((fieldSchema) => {
      const question = this.createQuestion(
        fieldSchema.type,
        fieldSchema.id,
        fieldSchema.label || '',
        !!fieldSchema.optional,
        group
      );

      const preloaded = this.savedDataLoader.getAnswer(question.id, group);
      if (preloaded !== null && preloaded !== undefined) {
        question.answer = preloaded;
      } else if (fieldSchema.value !== undefined) {
        question.answer = fieldSchema.value;
      }
    });

    return group;
  }

  public syncGroupSet(groupSetSchema: GroupSetSchema, count: number, section: Section): void {
    const existingGroups = section.groups.filter((g) => g.id === groupSetSchema.group.id);
    const currentCount = existingGroups.length;

    if (count > currentCount) {
      for (let i = currentCount; i < count; i++) {
        this.createGroupFromSchema(groupSetSchema.group, i, section);
      }
    } else if (count < currentCount) {
      for (let i = currentCount - 1; i >= count; i--) {
        this.removeGroup(existingGroups[i]);
      }
    }
  }

  public getFormGuid(): string {
    return this.form.guid;
  }

  public getFormReference(): string {
    const parts = this.getFormGuid().split('_');

    if (parts.length === 2) {
      return parts[0];
    }

    return '';
  }

  public getFormData(): Form {
    return this.form;
  }

  public getPreloadedAnswer(questionId: string, group: Group): unknown {
    return this.savedDataLoader.getAnswer(questionId, group);
  }

  public getPreloadedNumOfGroups(groupId: string, def: number = 0): number {
    return this.savedDataLoader.getNumOfGroups(groupId, def);
  }

  public setDeclaration(declaration: Form['declaration']): void {
    this.form.declaration = declaration;
  }

  public setAcceptPaymentTermsAndConditions(val: boolean): void {
    this.form.acceptPaymentTermsAndConditions = val;
  }

  public createStep(
    id: string,
    label: string,
    title: string,
    navigationLabel: string | null = null
  ): Step {
    const step = new Step();

    step.id = id;
    step.label = label;
    step.title = title;
    step.navigationLabel = navigationLabel || title;

    this.form.steps.push(step);

    return step;
  }

  public createSection(id: string, label: string, step: Step | null): Section {
    const section = new Section();

    section.id = id;
    section.label = label;

    if (step) {
      step.sections.push(section);
    }

    return section;
  }

  public createGroup(id: string, label: string, index: number, section: Section | null): Group {
    const group = new Group();

    group.id = id;
    group.label = label;
    group.index = index;

    if (section) {
      section.groups.push(group);
    }

    return group;
  }

  public createQuestion(
    type: string,
    id: string,
    label: string,
    optional: boolean,
    group: Group | null
  ): Question {
    const question = new Question();

    question.type = type;
    question.id = id;
    question.label = label;
    question.optional = optional;
    question.answer = null;

    if (group) {
      group.questions.push(question);
    }

    return question;
  }

  public removeGroup(groupToRemove: Group): void {
    this.form.steps.forEach((step: Step) => {
      step.sections.forEach((section: Section) => {
        const index = section.groups.indexOf(groupToRemove);
        if (index !== -1) {
          section.groups.splice(index, 1);
        }
      });
    });
  }

  public setActiveStep(step: Step): void {
    this.activeStep = step;
  }

  public getActiveStep(): Step | null {
    return this.activeStep;
  }

  public setNextActiveStep(): void {
    const currentStepIndex = this.getStepIndex(this.activeStep!);

    if (currentStepIndex < this.form.steps.length - 1) {
      this.activeStep = this.form.steps[currentStepIndex + 1];
    }
  }

  public setPreviousActiveStep(): void {
    const currentStepIndex = this.getStepIndex(this.activeStep!);
    if (currentStepIndex > 0) {
      this.activeStep = this.form.steps[currentStepIndex - 1];
    }
  }

  public getStepIndex(targetStep: Step): number {
    return this.form.steps.findIndex((step) => step.id === targetStep.id);
  }

  public isActiveStep(step: Step): boolean {
    return this.activeStep !== null && this.activeStep.id === step.id;
  }

  public isFirstStep(step: Step): boolean {
    return this.getStepIndex(step) === 0;
  }

  public isFinalStep(step: Step): boolean {
    const stepIndex = this.getStepIndex(step);

    return stepIndex === this.form.steps.length - 1;
  }

  public setDefaultActiveStep(displayService: IDisplayService): void {
    let activeStep: Step | null = null;

    this.form.steps.forEach((step: Step) => {
      if (activeStep !== null) {
        return;
      }

      if (!step.isComplete(displayService) || this.isFinalStep(step)) {
        activeStep = step;
      }
    });

    if (activeStep) {
      this.setActiveStep(activeStep);
    }
  }

  public getQuestionById(id: string, group: Group | null = null): Question | null {
    if (group === null) {
      return this.getFirstQuestionsById(id);
    }

    return group.questions.find((question) => question.id === id) || null;
  }

  private getFirstQuestionsById(id: string): Question | null {
    const questions = this.getQuestionsById(id);

    if (questions.length < 1) {
      return null;
    }

    return questions[0];
  }

  public getQuestionsById(id: string): Question[] {
    const questions: Question[] = [];

    this.form.steps.forEach((step: Step) => {
      step.sections.forEach((section: Section) => {
        section.groups.forEach((group: Group) => {
          group.questions.forEach((question: Question) => {
            if (question.id === id) {
              questions.push(question);
            }
          });
        });
      });
    });

    return questions;
  }
}
